import { useEffect, useState } from "react";
import { api } from "./api";
import { useTranslation } from "./i18n";

type MenuItem = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  price_minor: number;
  currency: string;
  is_active: boolean;
};

type Attribute = { id: string; name: string; active: boolean };
type AttrCategory = { id: string; name: string; items: Attribute[] };

function loadAttributes(): AttrCategory[] {
  try {
    const raw = localStorage.getItem("restify.attributes");
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return [
    { id: "meat", name: "Meat", items: [
      { id: "m1", name: "Chicken", active: true },
      { id: "m2", name: "Beef", active: true },
      { id: "m3", name: "Lamb", active: true },
      { id: "m4", name: "Mix", active: true },
    ]},
    { id: "sauce", name: "Sauce", items: [
      { id: "s1", name: "Garlic", active: true },
      { id: "s2", name: "Hot", active: true },
      { id: "s3", name: "Mild", active: true },
      { id: "s4", name: "BBQ", active: true },
    ]},
    { id: "extras", name: "Extras", items: [
      { id: "e1", name: "Extra cheese", active: true },
      { id: "e2", name: "Jalapeños", active: true },
      { id: "e3", name: "Feta", active: true },
    ]},
    { id: "dough", name: "Dough", items: [
      { id: "d1", name: "Thin", active: true },
      { id: "d2", name: "Thick", active: true },
      { id: "d3", name: "Lavash", active: true },
    ]},
  ];
}

function saveAttributes(cats: AttrCategory[]) {
  localStorage.setItem("restify.attributes", JSON.stringify(cats));
}

type Props = {
  restaurantId: string;
  onToast: (t: { title: string; body: string; type: "success" | "error" | "info" }) => void;
};

export function MenuPage({ restaurantId, onToast }: Props) {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState<"products" | "attributes" | "sync">("products");
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formSku, setFormSku] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [categories, setCategories] = useState<AttrCategory[]>(loadAttributes);
  const [newCatName, setNewCatName] = useState("");
  const [newAttrName, setNewAttrName] = useState("");
  const [addingAttrCat, setAddingAttrCat] = useState<string | null>(null);
  const [syncedItems, setSyncedItems] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncedPlatforms, setSyncedPlatforms] = useState<string[]>([]);

  useEffect(() => {
    api.listMenu(restaurantId)
      .then(setItems)
      .catch((err: Error) => onToast({ title: t("toast.error"), body: err.message, type: "error" }))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  const handleAddProduct = async () => {
    if (!formName || !formSku) return;
    try {
      const item = await api.createMenuItem(restaurantId, {
        name: formName,
        sku: formSku,
        price_minor: parseInt(formPrice) || 0,
        description: formDesc || null,
      });
      setItems((prev) => [...prev, item]);
      setShowForm(false);
      setFormName(""); setFormSku(""); setFormPrice(""); setFormDesc("");
      onToast({ title: "Product added", body: formName, type: "success" });
    } catch (err: unknown) {
      onToast({ title: t("toast.error"), body: err instanceof Error ? err.message : String(err), type: "error" });
    }
  };

  const handleToggle = async (item: MenuItem) => {
    try {
      const updated = await api.updateMenuItem(item.id, { is_active: !item.is_active });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch (err: unknown) {
      onToast({ title: t("toast.error"), body: err instanceof Error ? err.message : String(err), type: "error" });
    }
  };

  const handleDelete = async (item: MenuItem) => {
    try {
      await api.deleteMenuItem(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      onToast({ title: "Deleted", body: item.name, type: "info" });
    } catch (err: unknown) {
      onToast({ title: t("toast.error"), body: err instanceof Error ? err.message : String(err), type: "error" });
    }
  };

  const toggleAttr = (catId: string, attrId: string) => {
    const updated = categories.map((c) =>
      c.id === catId
        ? { ...c, items: c.items.map((a) => (a.id === attrId ? { ...a, active: !a.active } : a)) }
        : c,
    );
    setCategories(updated);
    saveAttributes(updated);
  };

  const addCategory = () => {
    if (!newCatName.trim()) return;
    const updated = [...categories, { id: crypto.randomUUID(), name: newCatName.trim(), items: [] }];
    setCategories(updated);
    saveAttributes(updated);
    setNewCatName("");
  };

  const addAttribute = (catId: string) => {
    if (!newAttrName.trim()) return;
    const updated = categories.map((c) =>
      c.id === catId
        ? { ...c, items: [...c.items, { id: crypto.randomUUID(), name: newAttrName.trim(), active: true }] }
        : c,
    );
    setCategories(updated);
    saveAttributes(updated);
    setNewAttrName("");
    setAddingAttrCat(null);
  };

  const removeAttr = (catId: string, attrId: string) => {
    const updated = categories.map((c) =>
      c.id === catId ? { ...c, items: c.items.filter((a) => a.id !== attrId) } : c,
    );
    setCategories(updated);
    saveAttributes(updated);
  };

  const syncFromPlatforms = async () => {
    setSyncing(true);
    try {
      const res = await api.syncMenu(restaurantId);
      setSyncedItems(res.items || []);
      setSyncedPlatforms(res.platforms_synced || []);
      onToast({ title: "Menu Synced", body: `${(res.items || []).length} items from ${(res.platforms_synced || []).length} platform(s)`, type: "success" });
    } catch (err: any) {
      onToast({ title: t("toast.error"), body: err.message || "Sync failed", type: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const PLATFORM_BADGES: Record<string, string> = { wolt: "🔵 Wolt", uber_eats: "🟢 Uber", glovo: "🟡 Glovo", bolt_food: "🟣 Bolt", pyszne: "🟠 Pyszne" };

  return (
    <>
      <div className="tabs" style={{ marginBottom: 0 }}>
        <div className={`tab ${subTab === "products" ? "tab-active" : ""}`} onClick={() => setSubTab("products")}>
          {t("menu.products")}
        </div>
        <div className={`tab ${subTab === "attributes" ? "tab-active" : ""}`} onClick={() => setSubTab("attributes")}>
          {t("menu.attributes")}
        </div>
        <div className={`tab ${subTab === "sync" ? "tab-active" : ""}`} onClick={() => setSubTab("sync")}>
          🔄 Platform Sync
        </div>
      </div>

      {subTab === "products" ? (
        <div className="card">
          <div className="orders-header">
            <h2>{t("menu.products")}</h2>
            <button className="btn btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={() => setShowForm(true)}>
              {t("menu.add_product")}
            </button>
          </div>

          {showForm && (
            <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 12, padding: 20, marginBottom: 20, border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label className="label">{t("menu.name")}</label>
                  <input className="input" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Kebab L" />
                </div>
                <div>
                  <label className="label">{t("menu.sku")}</label>
                  <input className="input" value={formSku} onChange={(e) => setFormSku(e.target.value)} placeholder="KEB-L" />
                </div>
                <div>
                  <label className="label">{t("menu.price")}</label>
                  <input className="input" type="number" value={formPrice} onChange={(e) => setFormPrice(e.target.value)} placeholder="2500" />
                </div>
                <div>
                  <label className="label">{t("menu.description")}</label>
                  <input className="input" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Large kebab with…" />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button className="btn btn-secondary" onClick={() => setShowForm(false)}>{t("menu.cancel")}</button>
                <button className="btn btn-primary" onClick={handleAddProduct}>{t("menu.save")}</button>
              </div>
            </div>
          )}

          {loading ? (
            <div><div className="loading-spinner" /><p className="muted" style={{ textAlign: "center" }}>{t("generic.loading")}</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🍽️</div><p className="muted">{t("menu.empty")}</p></div>
          ) : (
            <div className="menu-table">
              <div className="menu-table-header">
                <span>{t("menu.name")}</span>
                <span>{t("menu.sku")}</span>
                <span>{t("menu.price")}</span>
                <span>{t("menu.active")}</span>
                <span>{t("menu.actions")}</span>
              </div>
              {items.map((item) => (
                <div key={item.id} className="menu-table-row">
                  <span style={{ fontWeight: 500 }}>{item.name}</span>
                  <span className="muted">{item.sku}</span>
                  <span>{(item.price_minor / 100).toFixed(2)} {item.currency}</span>
                  <span>
                    <input type="checkbox" className="toggle-switch" checked={item.is_active} onChange={() => handleToggle(item)} />
                  </span>
                  <span>
                    <button className="btn btn-danger-sm" onClick={() => handleDelete(item)}>🗑️</button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : subTab === "attributes" ? (
        <div className="card">
          <div className="orders-header">
            <h2>{t("menu.attributes")}</h2>
          </div>
          <p className="muted" style={{ marginBottom: 20 }}>Configure product modifiers (meat, sauce, extras, dough type).</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {categories.map((cat) => (
              <div key={cat.id} className="attr-category">
                <div className="attr-category-header">
                  <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{cat.name}</span>
                  <button
                    className="btn"
                    style={{ fontSize: 11, padding: "4px 10px" }}
                    onClick={() => { setAddingAttrCat(addingAttrCat === cat.id ? null : cat.id); setNewAttrName(""); }}
                  >
                    {t("menu.add_attribute")}
                  </button>
                </div>

                {addingAttrCat === cat.id && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <input
                      className="input"
                      style={{ flex: 1 }}
                      placeholder={t("menu.attr_name")}
                      value={newAttrName}
                      onChange={(e) => setNewAttrName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addAttribute(cat.id)}
                    />
                    <button className="btn btn-primary" style={{ padding: "8px 12px", fontSize: 12 }} onClick={() => addAttribute(cat.id)}>
                      {t("menu.save")}
                    </button>
                  </div>
                )}

                <div className="attr-chips">
                  {cat.items.map((attr) => (
                    <div key={attr.id} className={`attr-chip ${attr.active ? "attr-active" : "attr-inactive"}`}>
                      <span onClick={() => toggleAttr(cat.id, attr.id)} style={{ cursor: "pointer" }}>
                        {attr.active ? "✅" : "⬜"} {attr.name}
                      </span>
                      <button className="attr-remove" onClick={() => removeAttr(cat.id, attr.id)}>×</button>
                    </div>
                  ))}
                  {cat.items.length === 0 && <span className="muted" style={{ fontSize: 12 }}>No attributes yet</span>}
                </div>
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input
                className="input"
                style={{ flex: 1, maxWidth: 240 }}
                placeholder={t("menu.category_name")}
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
              />
              <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={addCategory}>
                {t("menu.add_category")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="orders-header">
            <h2>🔄 Platform Menu Sync</h2>
            <button className="btn btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={syncFromPlatforms} disabled={syncing}>
              {syncing ? "⏳ Syncing..." : "🔄 Sync Now"}
            </button>
          </div>
          {syncedPlatforms.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {syncedPlatforms.map(p => <span key={p} className="platform-badge">{PLATFORM_BADGES[p] || p}</span>)}
            </div>
          )}
          {syncedItems.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🔄</div><p className="muted">Click Sync to pull menus from connected platforms</p></div>
          ) : (
            <div className="menu-table">
              <div className="menu-table-header">
                <span>Name</span>
                <span>Platform</span>
                <span>Price</span>
                <span>Status</span>
              </div>
              {syncedItems.map((item: any, i: number) => (
                <div key={i} className="menu-table-row">
                  <span style={{ fontWeight: 500 }}>{item.name}</span>
                  <span><span className="platform-badge" style={{ fontSize: 11 }}>{PLATFORM_BADGES[item.platform] || item.platform}</span></span>
                  <span>{(item.price_minor / 100).toFixed(2)}</span>
                  <span style={{ color: item.available ? "var(--success)" : "var(--danger)" }}>
                    {item.available ? "✅ Available" : "❌ Unavailable"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
